import { useTranslation } from 'react-i18next'
import mapValues from 'lodash-es/mapValues'

import { BirdviewActivitiesWidget } from '../../../BirdviewActivitiesWidget'
import { BirdviewAttentionWidget } from '../../../BirdviewAttentionWidget'
import { BirdviewCommunicationWidget } from '../../../BirdviewCommunicationWidget'
import { BirdviewDocFlowsWidget } from '../../../BirdviewDocFlowsWidget'
import { BirdviewGretaWidget } from '../../../BirdviewGretaWidget'
import { BirdviewPropertiesWidget } from '../../../BirdviewPropertiesWidget'
import { BirdviewServiceRequestsWidget } from '../../../BirdviewServiceRequestsWidget'
import { BirdviewTeamLoadWidget } from '../../../BirdviewTeamLoadWidget'
import { BIRDVIEW_SECTION_TARGET } from '../../../DashboardBirdview.constants.targets'
import { checkIsBirdviewTeamScope } from '../../../DashboardBirdview.utils'
import { BirdviewOversightWidget } from '../../BirdviewOversight.constants'
import {
  getBirdviewOversightSections,
  getBirdviewOversightWidgetTitleKeys,
} from '../../BirdviewOversight.utils'

export function useBirdviewOversightWidgets({
  activityDoneById,
  pendingIds,
  requestActions,
  scope,
  sections,
  onActivityToggle,
  onLoadAttentionGroup,
  onNavigate,
}) {
  const { t } = useTranslation()

  const isTeamScope = checkIsBirdviewTeamScope(scope)

  const {
    activities,
    attention,
    communication,
    criticalRequests,
    docFlows,
    greta,
    portfolio,
    teamLoad,
    tiles,
  } = getBirdviewOversightSections(sections)

  const widgetTitles = mapValues(
    getBirdviewOversightWidgetTitleKeys(isTeamScope),
    key => t(key)
  )

  const widgets = {
    [BirdviewOversightWidget.Activities]: dragHandleProps => (
      <BirdviewActivitiesWidget
        activityDoneById={activityDoneById}
        dragHandleProps={dragHandleProps}
        pendingIds={pendingIds}
        section={activities}
        title={widgetTitles[BirdviewOversightWidget.Activities]}
        onActivityToggle={onActivityToggle}
        onNavigate={onNavigate}
      />
    ),
    // Every attention row keeps its own age filter and the response loaded
    // for it, so the card is remounted to drop the data of the previous scope.
    [BirdviewOversightWidget.Attention]: dragHandleProps => (
      <BirdviewAttentionWidget
        dragHandleProps={dragHandleProps}
        key={scope}
        requestActions={requestActions}
        section={attention}
        title={widgetTitles[BirdviewOversightWidget.Attention]}
        onLoadAttentionGroup={onLoadAttentionGroup}
        onNavigate={onNavigate}
      />
    ),
    [BirdviewOversightWidget.Communication]: dragHandleProps => (
      <BirdviewCommunicationWidget
        dragHandleProps={dragHandleProps}
        section={communication}
        title={widgetTitles[BirdviewOversightWidget.Communication]}
        onNavigate={onNavigate}
      />
    ),
    [BirdviewOversightWidget.CriticalRequests]: dragHandleProps => (
      <BirdviewServiceRequestsWidget
        dragHandleProps={dragHandleProps}
        requestActions={requestActions}
        section={criticalRequests}
        showAllTarget={BIRDVIEW_SECTION_TARGET.criticalRequests}
        title={widgetTitles[BirdviewOversightWidget.CriticalRequests]}
        onNavigate={onNavigate}
      />
    ),
    [BirdviewOversightWidget.DocFlows]: dragHandleProps => (
      <BirdviewDocFlowsWidget
        dragHandleProps={dragHandleProps}
        section={docFlows}
        title={widgetTitles[BirdviewOversightWidget.DocFlows]}
        onNavigate={onNavigate}
      />
    ),
    [BirdviewOversightWidget.Greta]: dragHandleProps => (
      <BirdviewGretaWidget
        dragHandleProps={dragHandleProps}
        section={greta}
        title={widgetTitles[BirdviewOversightWidget.Greta]}
      />
    ),
    [BirdviewOversightWidget.Portfolio]: dragHandleProps => (
      <BirdviewPropertiesWidget
        dragHandleProps={dragHandleProps}
        section={portfolio}
        showAllTarget={BIRDVIEW_SECTION_TARGET.properties}
        title={widgetTitles[BirdviewOversightWidget.Portfolio]}
        onNavigate={onNavigate}
      />
    ),
    [BirdviewOversightWidget.TeamLoad]: dragHandleProps => (
      <BirdviewTeamLoadWidget
        dragHandleProps={dragHandleProps}
        section={teamLoad}
        title={widgetTitles[BirdviewOversightWidget.TeamLoad]}
        onNavigate={onNavigate}
      />
    ),
  }

  return { tiles, widgets, widgetTitles }
}
